import { useState } from 'react';
import {
  LayoutDashboard,
  FileText,
  Users,
  Menu,
  X,
  Shield,
  Home,
  Settings,
  BookOpen,
  ClipboardCheck,
  AlertTriangle,
} from 'lucide-react';

export type AdminView = 'dashboard' | 'reports' | 'users' | 'settings' | 'guide' | 'training' | 'incidents';

interface AdminSidebarProps {
  currentView: AdminView;
  onViewChange: (view: AdminView) => void;
  onBackToHome: () => void;
  userRole: 'root' | 'admin';
  userName?: string;
  userPhoto?: string | null;
}

export const AdminSidebar = ({
  currentView,
  onViewChange,
  onBackToHome,
  userRole,
  userName,
  userPhoto,
}: AdminSidebarProps) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const menuItems: Array<{
    id: AdminView;
    label: string;
    icon: React.ReactNode;
    availableFor: ('root' | 'admin')[];
  }> = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard size={20} />,
      availableFor: ['root', 'admin'],
    },
    {
      id: 'reports',
      label: 'Report Moderation',
      icon: <FileText size={20} />,
      availableFor: ['root', 'admin'],
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <Users size={20} />,
      availableFor: ['root'], // Only root admins can manage users
    },
    {
      id: 'settings',
      label: 'App Settings',
      icon: <Settings size={20} />,
      availableFor: ['root', 'admin'],
    },
    {
      id: 'guide',
      label: 'Moderation Guide',
      icon: <BookOpen size={20} />,
      availableFor: ['root', 'admin'],
    },
    {
      id: 'training',
      label: 'Training Checklist',
      icon: <ClipboardCheck size={20} />,
      availableFor: ['root', 'admin'],
    },
    {
      id: 'incidents',
      label: 'Incident Response',
      icon: <AlertTriangle size={20} />,
      availableFor: ['root', 'admin'],
    },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.availableFor.includes(userRole)
  );

  const handleViewChange = (view: AdminView) => {
    onViewChange(view);
    setIsMobileOpen(false); // Close mobile menu after selection
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-blue-600 text-white p-2 rounded-lg shadow-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
        aria-label="Open admin menu"
      >
        <Menu size={24} />
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - Fixed position */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40
          w-64 bg-gray-900 text-white
          transform transition-transform duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col h-screen
          shadow-xl
        `}
        aria-label="Admin navigation"
      >
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-800 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="text-blue-400" size={24} />
              <h2 className="font-bold text-lg">Admin Panel</h2>
            </div>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white transition-colors"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          {/* User Info */}
          {userName && (
            <div className="flex items-center gap-3 pt-4 border-t border-gray-800">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                {userPhoto ? (
                  <img
                    src={userPhoto}
                    alt={userName}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-white font-semibold text-sm">
                    {userName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{userName}</p>
                <p className="text-xs text-gray-400 truncate">
                  {userRole === 'root' ? 'Root Admin' : 'Admin'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Menu - Scrollable area */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1 min-h-0">
          {filteredMenuItems.map((item) => {
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleViewChange(item.id)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-colors duration-200
                  ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                {item.icon}
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer - Fixed at bottom of sidebar */}
        <div className="p-4 border-t border-gray-800 shrink-0">
          <button
            onClick={onBackToHome}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            <Home size={20} />
            <span className="font-medium">Back to Home</span>
          </button>
        </div>
      </aside>
    </>
  );
};
